import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../api';
import { toast } from 'react-toastify';

const CopySubjectsModal = ({
  isOpen,
  onClose,
  tenantId,
  department,
  currentAcademicContext,
  refreshData
}) => {
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [sourceSemesters, setSourceSemesters] = useState([]);
  const [targetSemesters, setTargetSemesters] = useState([]);
  const [sourceTerms, setSourceTerms] = useState([]);
  const [targetTerms, setTargetTerms] = useState([]);
  const [sourceContext, setSourceContext] = useState({
    academicYearId: '',
    semesterId: '',
    termId: ''
  });
  const [targetContext, setTargetContext] = useState({
    academicYearId: '',
    semesterId: '',
    termId: ''
  });
  const [sourceSubjects, setSourceSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch terms for an academic year - FIXED: removed tenantId parameter as it's not needed
  const fetchTerms = useCallback(
    async (academicYearId, setTermsState) => {
      if (!academicYearId) {
        setTermsState([]);
        return;
      }

      try {
        // Use the correct endpoint - tenantId is in query params
        const res = await api.get(`/api/academic-year/${academicYearId}/terms?tenantId=${tenantId}`);
        const terms = res.data.terms || [];
        console.log('Fetched terms:', terms);
        setTermsState(terms);
      } catch (err) {
        console.warn('Error fetching terms:', err);
        setTermsState([]);
      }
    },
    [tenantId] // Add tenantId as dependency
  );

  // Fetch semesters
  const fetchSemesters = useCallback(
    async (academicYearId, setSemesterState) => {
      if (!academicYearId) {
        setSemesterState([]);
        return;
      }

      try {
        const res = await api.get(`/api/classManager/semesters/${academicYearId}`);
        const semesters = res.data.semesters || [];
        console.log('Fetched semesters:', semesters);
        setSemesterState(semesters);
      } catch (err) {
        console.warn('Error fetching semesters:', err);
        setSemesterState([]);
      }
    },
    []
  );

  // Fetch academic years
  const fetchAllAcademicYears = useCallback(async () => {
    if (!tenantId) return;

    try {
      const activeRes = await api.get(`/api/academic-year/all-active?tenantId=${tenantId}`);
      const completedRes = await api.get(`/api/academic-year/all-completed?tenantId=${tenantId}`);

      let activeYears = activeRes?.data?.academicYears || [];
      let completedYears = completedRes?.data?.academicYears || [];

      if (!Array.isArray(activeYears) && activeYears) activeYears = [activeYears];
      if (!Array.isArray(completedYears) && completedYears) completedYears = [completedYears];

      const allYears = [...activeYears, ...completedYears];
      console.log('Fetched academic years:', allYears);
      setAvailableAcademicYears(allYears);

      // Auto-select current context for source (sync with SubjectManager)
      if (currentAcademicContext?.academicYearId) {
        const currentYearExists = allYears.some(
          year => year._id === currentAcademicContext.academicYearId
        );
        if (currentYearExists) {
          setSourceContext({
            academicYearId: currentAcademicContext.academicYearId,
            semesterId: currentAcademicContext.semesterId || '',
            termId: currentAcademicContext.termId || ''
          });
          
          // Also fetch semesters/terms for the source
          const sourceYear = allYears.find(y => y._id === currentAcademicContext.academicYearId);
          if (sourceYear) {
            if (sourceYear.isSemesterBased) {
              fetchSemesters(currentAcademicContext.academicYearId, setSourceSemesters);
            } else {
              // FIXED: Pass only two arguments
              fetchTerms(currentAcademicContext.academicYearId, setSourceTerms);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching academic years:', err);
      toast.error('Failed to load academic years');
    }
  }, [tenantId, currentAcademicContext, fetchSemesters, fetchTerms]);

  // Fetch source subjects
  const fetchSourceSubjects = useCallback(async () => {
    if (!sourceContext.academicYearId || !department?._id) {
      setSourceSubjects([]);
      return;
    }

    const selectedYear = availableAcademicYears.find(
      year => year._id === sourceContext.academicYearId
    );

    // Validate based on academic year type
    if (selectedYear?.isSemesterBased) {
      if (!sourceContext.semesterId) {
        setSourceSubjects([]);
        return;
      }
    } else {
      if (!sourceContext.termId) {
        setSourceSubjects([]);
        return;
      }
    }

    setLoading(true);
    try {
      const params = {
        tenantId,
        academicYearId: sourceContext.academicYearId,
        departmentId: department._id
      };

      if (selectedYear?.isSemesterBased && sourceContext.semesterId) {
        params.semesterId = sourceContext.semesterId;
      } else if (!selectedYear?.isSemesterBased && sourceContext.termId) {
        params.termId = sourceContext.termId;
      }

      console.log('Fetching subjects with params:', params);

      const response = await api.get(`/api/subjects/get-subjects/${department._id}`, { params });

      let subjects = [];

      if (response.data?.subjects?.all) {
        subjects = response.data.subjects.all;
      } else if (response.data?.subjects) {
        subjects = response.data.subjects;
      } else if (response.data?.data?.subjects?.all) {
        subjects = response.data.data.subjects.all;
      } else if (Array.isArray(response.data)) {
        subjects = response.data;
      } else if (Array.isArray(response.data?.data)) {
        subjects = response.data.data;
      }

      console.log('Fetched source subjects:', subjects.length);
      setSourceSubjects(subjects);
      setSelectedSubjects(new Set());
    } catch (err) {
      console.error('Error fetching source subjects:', err);
      toast.error('Failed to load source subjects');
      setSourceSubjects([]);
    } finally {
      setLoading(false);
    }
  }, [sourceContext, department, availableAcademicYears, tenantId]);

  // Filter subjects based on search
  const filteredSubjects = useMemo(() => {
    if (!searchTerm) return sourceSubjects;

    return sourceSubjects.filter(subject =>
      subject.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sourceSubjects, searchTerm]);

  // Helper functions
  const isAcademicYearSemesterBased = useCallback(
    (academicYearId) => {
      const year = availableAcademicYears.find(y => y._id === academicYearId);
      return year?.isSemesterBased || false;
    },
    [availableAcademicYears]
  );

  const getAcademicYearById = useCallback(
    (academicYearId) => {
      return availableAcademicYears.find(y => y._id === academicYearId);
    },
    [availableAcademicYears]
  );

  const getActiveAcademicYears = useCallback(() => {
    return availableAcademicYears.filter(year => year.status !== 'completed');
  }, [availableAcademicYears]);

  const isSourceContextValid = useCallback(() => {
    if (!sourceContext.academicYearId) return false;
    const selectedYear = getAcademicYearById(sourceContext.academicYearId);
    if (selectedYear?.isSemesterBased) {
      return !!sourceContext.semesterId;
    } else {
      return !!sourceContext.termId;
    }
  }, [sourceContext, getAcademicYearById]);



  // Handle context changes
  const handleSourceContextChange = (field, value) => {
    setSourceContext(prev => {
      const newContext = { ...prev, [field]: value };

      if (field === 'academicYearId') {
        newContext.semesterId = '';
        newContext.termId = '';
        setSourceSubjects([]);
        setSelectedSubjects(new Set());
        setSearchTerm('');
        
        // Fetch appropriate data based on year type
        const selectedYear = getAcademicYearById(value);
        if (selectedYear?.isSemesterBased) {
          fetchSemesters(value, setSourceSemesters);
          setSourceTerms([]);
        } else {
          // FIXED: Pass only two arguments
          fetchTerms(value, setSourceTerms);
          setSourceSemesters([]);
        }
      }

      return newContext;
    });
  };

  const handleTargetContextChange = (field, value) => {
    setTargetContext(prev => {
      const newContext = { ...prev, [field]: value };

      if (field === 'academicYearId') {
        newContext.semesterId = '';
        newContext.termId = '';
        
        // Fetch appropriate data based on year type
        const selectedYear = getAcademicYearById(value);
        if (selectedYear?.isSemesterBased) {
          fetchSemesters(value, setTargetSemesters);
          setTargetTerms([]);
        } else {
          // FIXED: Pass only two arguments
          fetchTerms(value, setTargetTerms);
          setTargetSemesters([]);
        }
      }

      return newContext;
    });
  };

  // Selection handlers
  const toggleSubjectSelection = (subjectId) => {
    setSelectedSubjects(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(subjectId)) {
        newSelection.delete(subjectId);
      } else {
        newSelection.add(subjectId);
      }
      return newSelection;
    });
  };

  const selectAllFiltered = () => {
    setSelectedSubjects(new Set(filteredSubjects.map(s => s._id)));
  };

  const deselectAll = () => {
    setSelectedSubjects(new Set());
  };

  const toggleAllFiltered = () => {
    if (selectedSubjects.size === filteredSubjects.length) {
      deselectAll();
    } else {
      selectAllFiltered();
    }
  };

  // Validation
  const validateTargetContext = () => {
    if (!targetContext.academicYearId) {
      toast.error('Please select target academic year');
      return false;
    }

    const targetYear = getAcademicYearById(targetContext.academicYearId);
    if (targetYear?.isSemesterBased && !targetContext.semesterId) {
      toast.error('Please select target semester');
      return false;
    }
    if (!targetYear?.isSemesterBased && !targetContext.termId) {
      toast.error('Please select target term');
      return false;
    }

    // Check if source and target are the same
    const sameAcademicYear = sourceContext.academicYearId === targetContext.academicYearId;
    if (sameAcademicYear) {
      const sourceYear = getAcademicYearById(sourceContext.academicYearId);
      if (sourceYear?.isSemesterBased) {
        if (sourceContext.semesterId === targetContext.semesterId) {
          toast.error('Source and target contexts cannot be the same');
          return false;
        }
      } else {
        if (sourceContext.termId === targetContext.termId) {
          toast.error('Source and target contexts cannot be the same');
          return false;
        }
      }
    }

    return true;
  };

  // Copy subjects
  const copySubjects = async () => {
    if (selectedSubjects.size === 0) {
      toast.error('Please select at least one subject to copy');
      return;
    }

    if (!validateTargetContext()) return;

    setCopying(true);
    try {
      const sourceYear = getAcademicYearById(sourceContext.academicYearId);
      const targetYear = getAcademicYearById(targetContext.academicYearId);

      const payload = {
        tenantId,
        departmentId: department._id,
        sourceAcademicYearId: sourceContext.academicYearId,
        sourceSemesterId: sourceYear?.isSemesterBased ? sourceContext.semesterId || null : null,
        sourceTermId: !sourceYear?.isSemesterBased ? sourceContext.termId || null : null,
        targetAcademicYearId: targetContext.academicYearId,
        targetSemesterId: targetYear?.isSemesterBased ? targetContext.semesterId || null : null,
        targetTermId: !targetYear?.isSemesterBased ? targetContext.termId || null : null,
        subjectIds: Array.from(selectedSubjects),
        copyFacultyAssignments: true
      };

      console.log('Copy subjects payload:', payload);

      const response = await api.post('/api/subjects/copy-subjects', payload);

      toast.success(`Successfully copied ${response.data.copiedCount} subjects`);
      setSelectedSubjects(new Set());
      if (refreshData) refreshData();
      onClose();
    } catch (err) {
      console.error('Error copying subjects:', err);
      const errorMsg = err.response?.data?.message || 'Failed to copy subjects';
      toast.error(errorMsg);
    } finally {
      setCopying(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAllAcademicYears();
      setSourceContext({ academicYearId: '', semesterId: '', termId: '' });
      setTargetContext({ academicYearId: '', semesterId: '', termId: '' });
      setSourceSubjects([]);
      setSelectedSubjects(new Set());
      setSearchTerm('');
      setSourceSemesters([]);
      setTargetSemesters([]);
      setSourceTerms([]);
      setTargetTerms([]);
    }
  }, [isOpen, fetchAllAcademicYears]);

  // Fetch subjects when source context changes
  useEffect(() => {
    if (isSourceContextValid()) {
      fetchSourceSubjects();
    } else {
      setSourceSubjects([]);
      setSelectedSubjects(new Set());
    }
  }, [sourceContext, isSourceContextValid, fetchSourceSubjects]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="relative inline-block w-full max-w-4xl text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Copy Subjects</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Copy subjects from one academic context to another
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Department Info */}
          {department && (
            <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>Department: <strong>{department.name}</strong></span>
              </div>
            </div>
          )}

          {/* Main Content - Scrollable */}
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Context Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Source Context */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-lg">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Copy From</h4>
                      <p className="text-sm text-gray-600">Select source context</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Source Academic Year Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
                      <select
                        value={sourceContext.academicYearId}
                        onChange={(e) => handleSourceContextChange('academicYearId', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">Select Academic Year</option>
                        {availableAcademicYears.map(year => (
                          <option key={year._id} value={year._id}>
                            {year.name}{year.label ? ` (${year.label})` : ''} {year.status === 'completed' ? '(Completed)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Source Semester Dropdown (for semester-based) */}
                    {sourceContext.academicYearId && isAcademicYearSemesterBased(sourceContext.academicYearId) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Semester</label>
                        <select
                          value={sourceContext.semesterId}
                          onChange={(e) => handleSourceContextChange('semesterId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="">Select Semester</option>
                          {sourceSemesters.map(semester => (
                            <option key={semester._id} value={semester._id}>
                              {semester.semesterName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Source Term Dropdown (for term-based) */}
                    {sourceContext.academicYearId && !isAcademicYearSemesterBased(sourceContext.academicYearId) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                        <select
                          value={sourceContext.termId}
                          onChange={(e) => handleSourceContextChange('termId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select Term</option>
                          {sourceTerms.map(term => (
                            <option key={term._id} value={term._id}>
                              {term.termLabel || term.termName || `Term ${term.termNumber}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Target Context */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-lg">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Copy To</h4>
                      <p className="text-sm text-gray-600">Select target context</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Target Academic Year Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
                      <select
                        value={targetContext.academicYearId}
                        onChange={(e) => handleTargetContextChange('academicYearId', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Select Academic Year</option>
                        {getActiveAcademicYears().map(year => (
                          <option key={year._id} value={year._id}>
                            {year.name}{year.label ? ` (${year.label})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Target Semester Dropdown (for semester-based) */}
                    {targetContext.academicYearId && isAcademicYearSemesterBased(targetContext.academicYearId) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Semester</label>
                        <select
                          value={targetContext.semesterId}
                          onChange={(e) => handleTargetContextChange('semesterId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select Semester</option>
                          {targetSemesters.map(semester => (
                            <option key={semester._id} value={semester._id}>
                              {semester.semesterName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Target Term Dropdown (for term-based) */}
                    {targetContext.academicYearId && !isAcademicYearSemesterBased(targetContext.academicYearId) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                        <select
                          value={targetContext.termId}
                          onChange={(e) => handleTargetContextChange('termId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select Term</option>
                          {targetTerms.map(term => (
                            <option key={term._id} value={term._id}>
                              {term.termLabel || term.termName || `Term ${term.termNumber}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subjects Section */}
              {isSourceContextValid() && (
                <div className="border border-gray-200 rounded-lg">
                  {/* Subjects Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Available Subjects</h4>
                        <p className="text-sm text-gray-600">
                          {sourceSubjects.length} total • {filteredSubjects.length} shown • {selectedSubjects.size} selected
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search subjects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                          />
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Bulk Actions */}
                    {filteredSubjects.length > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedSubjects.size === filteredSubjects.length && filteredSubjects.length > 0}
                            onChange={toggleAllFiltered}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {selectedSubjects.size === filteredSubjects.length ? 'All selected' :
                              selectedSubjects.size > 0 ? `${selectedSubjects.size} selected` : 'Select all'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={selectAllFiltered}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            disabled={selectedSubjects.size === filteredSubjects.length}
                          >
                            Select All
                          </button>
                          <button
                            onClick={deselectAll}
                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                            disabled={selectedSubjects.size === 0}
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subjects List */}
                  <div className="max-h-64 overflow-y-auto">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Loading subjects...</span>
                      </div>
                    ) : filteredSubjects.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No subjects match your search' : 'No subjects found'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredSubjects.map(subject => (
                          <div
                            key={subject._id}
                            className={`p-4 hover:bg-gray-50 transition-colors ${
                              selectedSubjects.has(subject._id) ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedSubjects.has(subject._id)}
                                onChange={() => toggleSubjectSelection(subject._id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900 text-sm truncate">
                                    {subject.name}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    {subject.subjectType || 'theory'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-600">
                                  <code className="bg-gray-100 px-2 py-1 rounded">{subject.subjectCode}</code>
                                  {subject.isCreditBased && (
                                    <span className="text-green-600">{subject.credits || 0} credits</span>
                                  )}
                                  {subject.isCrossSemester && (
                                    <span className="text-purple-600">Cross-semester</span>
                                  )}
                                  {subject.termId && !subject.isCrossSemester && (
                                    <span className="text-indigo-600">
                                      Term: {subject.termId?.termLabel || subject.termId?.termName || 'Term'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedSubjects.size > 0 && (
                  <span className="font-medium text-gray-900">
                    {selectedSubjects.size} subject(s) ready to copy
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={copying}
                >
                  Cancel
                </button>
                <button
                  onClick={copySubjects}
                  disabled={
                    copying ||
                    selectedSubjects.size === 0 ||
                    !targetContext.academicYearId ||
                    (isAcademicYearSemesterBased(targetContext.academicYearId) && !targetContext.semesterId) ||
                    (!isAcademicYearSemesterBased(targetContext.academicYearId) && !targetContext.termId)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
                >
                  {copying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy ({selectedSubjects.size})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopySubjectsModal;